import React, { useMemo, useState } from 'react';
import i18n from '../../../../../i18n';
import { fmt, METRICS } from '../../../data/metrics';
import type { DrillBasis } from '../../../types';
import { Icon } from '../../shared/Icon';
import { DrillTile } from './DrillTile';
import { DrillBarChart } from './charts/DrillBarChart';
import { DrillChartLegend } from './charts/DrillChartLegend';
import { shortOutletLabels } from './outletNames';

type OutletRow = {
  id: string;
  name: string;
  value: number;
  /** Compare-period value (present when `comparable`). */
  compareValue?: number;
};

export function DrillOutletSection({
  metricKey,
  outlets,
  secondaryOutlets,
  groupVal,
  fmtVal,
  fmtVal2,
  onSelectOutlet,
  span = 12,
  compareLabel,
  outletBasis,
}: {
  metricKey: string;
  outlets: OutletRow[];
  secondaryOutlets?: OutletRow[];
  groupVal: number;
  fmtVal: (v: number) => string;
  fmtVal2?: (v: number) => string;
  onSelectOutlet?: (outletId: string) => void;
  /** Half-width next to correlates; full-width when alone. */
  span?: 6 | 12;
  /** Reservations and guests behind each outlet row, by outlet id. */
  outletBasis?: Record<string, DrillBasis>;
  /** Legend label for compare chips, e.g. "vs 3 Jun – 30 Jun". */
  compareLabel?: string;
}) {
  const m = METRICS[metricKey];
  const goodHigh = m?.better === 'high';
  const isPctMetric = m?.unit === '%';
  const [worstFirst, setWorstFirst] = useState(false);
  const hasCompare = outlets.some(o => o.compareValue != null);

  const secondaryByOutlet = useMemo(() => {
    const map = new Map<string, number>();
    for (const row of secondaryOutlets ?? []) map.set(row.id, row.value);
    return map;
  }, [secondaryOutlets]);

  // "L'Osteria München Pasing" → "Pasing" when every outlet shares the prefix.
  const shortNameById = useMemo(() => {
    const labels = shortOutletLabels(outlets.map(o => o.name));
    return new Map(outlets.map((o, i) => [o.id, labels[i]]));
  }, [outlets]);

  const sorted = useMemo(() => {
    const arr = [...outlets];
    arr.sort((a, b) => (goodHigh ? b.value - a.value : a.value - b.value));
    return worstFirst ? arr.reverse() : arr;
  }, [outlets, goodHigh, worstFirst]);

  const fleetAvg =
    outlets.reduce((a, o) => a + o.value, 0) / (outlets.length || 1) ||
    groupVal ||
    1;

  const inspector = useMemo(
    () => (
      <>
        <div className="dp-head" style={{ marginBottom: 8 }}>
          <div className="dp-head-main" />
          <button
            type="button"
            className="cmp-sort"
            onClick={e => {
              e.stopPropagation();
              setWorstFirst(v => !v);
            }}
          >
            <Icon name="sliders" size={13} />
            {worstFirst
              ? i18n.t('performance:drill.worstFirst')
              : i18n.t('performance:drill.bestFirst')}
          </button>
        </div>
        <div className="cmp-list">
          {sorted.map((row, i) => {
            const rank = worstFirst ? sorted.length - i : i + 1;
            const tone =
              row.value >= fleetAvg
                ? goodHigh
                  ? 'var(--st-steady)'
                  : 'var(--st-alert)'
                : goodHigh
                  ? 'var(--st-alert)'
                  : 'var(--st-steady)';
            const v2 = secondaryByOutlet.get(row.id);
            const open = onSelectOutlet
              ? () => onSelectOutlet(row.id)
              : undefined;
            return (
              <div
                className={`cmp-row is-inspector${open ? ' clickable' : ''}`}
                key={row.id}
                title={row.name}
                onClick={
                  open
                    ? e => {
                        e.stopPropagation();
                        open();
                      }
                    : undefined
                }
                onKeyDown={
                  open
                    ? e => {
                        if (e.key !== 'Enter' && e.key !== ' ') return;
                        e.preventDefault();
                        e.stopPropagation();
                        open();
                      }
                    : undefined
                }
                role={open ? 'button' : undefined}
                tabIndex={open ? 0 : undefined}
              >
                <span className="cmp-rank">{rank}</span>
                <div className="cmp-name">
                  <b>{shortNameById.get(row.id) ?? row.name}</b>
                </div>
                <span className="cmp-val" style={{ color: tone }}>
                  <span>
                    {fmtVal(row.value)}
                    {v2 != null && fmtVal2 ? ` · ${fmtVal2(v2)}` : ''}
                  </span>
                  {hasCompare && row.compareValue != null ? (
                    <span
                      className="dcrux-cmp-chip"
                      title={compareLabel ? `${compareLabel}: ${fmtVal(row.compareValue)}` : i18n.t('performance:drillUi.vsCompare')}
                    >
                      {fmtVal(row.compareValue)}
                      {(() => {
                        const cmp = row.compareValue!;
                        const delta = isPctMetric
                          ? +(row.value - cmp).toFixed(1)
                          : cmp
                            ? +((row.value / cmp - 1) * 100).toFixed(1)
                            : null;
                        return delta != null
                          ? ` · ${delta >= 0 ? '+' : ''}${delta}${isPctMetric ? 'pt' : '%'}`
                          : '';
                      })()}
                    </span>
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
      </>
    ),
    [
      sorted,
      worstFirst,
      fleetAvg,
      goodHigh,
      isPctMetric,
      hasCompare,
      compareLabel,
      shortNameById,
      secondaryByOutlet,
      fmtVal,
      fmtVal2,
      onSelectOutlet,
    ],
  );

  if (outlets.length < 2) return null;

  const bestSorted = [...outlets].sort((a, b) =>
    goodHigh ? b.value - a.value : a.value - b.value,
  );
  // More rows in the half-width tile — bar thickness is capped in DrillBarChart.
  const tipCount = span === 6 ? 5 : 4;
  const cruxBest = bestSorted.slice(0, tipCount);
  const trailingPool = [...bestSorted]
    .reverse()
    .filter(r => !cruxBest.some(b => b.id === r.id));
  // Prefer non-zero trailing outlets so the list isn't padded with empty bars.
  const cruxWorst = [
    ...trailingPool.filter(r => r.value > 0),
    ...trailingPool.filter(r => r.value <= 0),
  ].slice(0, tipCount);

  const chartRows = [...cruxBest, ...cruxWorst];
  const chartData = chartRows.map(r => ({
    name: shortNameById.get(r.id) ?? r.name,
    fullName: r.name,
    value: r.value,
    color: cruxBest.some(b => b.id === r.id)
      ? 'var(--petrol-ink-light, #14606B)'
      : 'color-mix(in srgb, var(--st-alert) 55%, var(--st-quiet))',
  }));
  const outletHeight = Math.max(
    span === 6 ? 200 : 180,
    28 + chartData.length * 22,
  );

  return (
    <DrillTile
      sectionId="outlets"
      span={span}
      title={i18n.t('performance:drillUi.everyOutlet')}
      n={
        outletBasis
          ? metricKey === 'covers'
            ? i18n.t('performance:drillUi.nOutletsGuests', {
                guests: fmt.num(
                  outlets.reduce((a, o) => a + (outletBasis[o.id]?.guests ?? 0), 0),
                ),
                count: outlets.length,
              })
            : i18n.t('performance:drillUi.nOutlets', {
                reservations: fmt.num(
                  outlets.reduce((a, o) => a + (outletBasis[o.id]?.reservations ?? 0), 0),
                ),
                count: outlets.length,
              })
          : undefined
      }
      meta={
        // The bars draw one metric per outlet; a paired metric sits in Details.
        i18n.t('performance:drillUi.outletsTopBottom', { count: outlets.length })
      }
      inspector={inspector}
    >
      <DrillChartLegend
        items={[
          { name: i18n.t('performance:drillUi.leading'), color: 'var(--petrol-ink-light, #14606B)' },
          {
            name: i18n.t('performance:drillUi.trailing'),
            color: 'color-mix(in srgb, var(--st-alert) 55%, var(--st-quiet))',
          },
        ]}
        dense
      />
      <DrillBarChart
        data={chartData}
        fmtVal={fmtVal}
        height={outletHeight}
        horizontal
        showValues
        fill={span === 6}
        maxHeight={Math.max(outletHeight, 48 + chartData.length * 40)}
      />
    </DrillTile>
  );
}
